import React, { useState, useMemo, useEffect, useRef, useContext } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import ApiService from "../../../../services/ApiService";
import { useAuth } from "../../../Providers/LandmarkAuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../../../context/ThemeContext";

const AUTH_DOMAIN = "landmarkventures.com";
const PREFIX_RE = /^[a-zA-Z0-9._%+-]+$/;
// Landmark Review sites allowed to sign people in here when they frame this app
const REVIEW_ORIGINS = [
  process.env.REACT_APP_REVIEW_ORIGIN,
  "https://review.landmarkventures.com",
  ...(["localhost", "127.0.0.1"].includes(window.location.hostname) ? ["http://localhost:3100"] : []),
]
  .filter(Boolean)
  .map((o) => o.replace(/\/+$/, ""));

const AuthPage = ({
  endpoint = "landmark/employees/auth/sendLoginLink",
  apiOptions = undefined,
}) => {

  const [emailPrefix, setEmailPrefix] = useState("");
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false); // sending email state
  const [verifying, setVerifying] = useState(false); // verifying magic link state

  const { refreshAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
const { changeBackground } = useContext(ThemeContext);

const didSetThemeRef = useRef(false);
const isLocalClient = useMemo(
  () => ["localhost", "127.0.0.1"].includes(window.location.hostname),
  []
);
useEffect(() => {
  if (didSetThemeRef.current) return; // already ran
  didSetThemeRef.current = true;

  changeBackground({ value: "light", label: "Light" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []); // intentionally empty so it runs once on mount

  console.log("[AuthPage] render (user is not authenticated)");

  // Parse token & redirect from the current URL (runs on first mount too)
  const token = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("token");
  }, [location.search]);

  const rParam = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("r");
  }, [location.search]);

  // Only verify once per token value
  const lastVerifiedTokenRef = useRef(null);

  // Build the intended return path from where the user is right now (used when requesting link)
  const intendedRedirect = useMemo(() => {
    const path = (location.pathname || "/") + (location.search || "") + (location.hash || "");
    return path.startsWith("/") ? path : "/landmark";
  }, [location.pathname, location.search, location.hash]);

  // === VERIFY ON PAGE LOAD (and whenever ?token changes) ===
  useEffect(() => {
    // Run ONLY when a token exists and hasn't been processed yet
    if (!token) return;
    if (lastVerifiedTokenRef.current === token) return;

    lastVerifiedTokenRef.current = token;
    setVerifying(true);

    (async () => {
      try {
        console.log("[AuthPage] Found token in URL. Verifying…");
        const api = new ApiService();
        const resp = await api.post("landmark/employees/auth/verifyMagicLink", { token });

        if (!resp?.success) {
          lastVerifiedTokenRef.current = null; // allow retry if needed
          toast.error(resp?.error || "Verification failed. Please request a new link.");
          return;
        }

        // Optional fallback: if your backend returns sessionToken, persist it so /auth/me can work
        if (resp.sessionToken) {
          localStorage.setItem("token", resp.sessionToken);
        }

        // Prefer server-provided redirect (sanitized), else ?r=, else default
        const nextPath =
          (resp.redirect && typeof resp.redirect === "string" && resp.redirect.startsWith("/")
            ? resp.redirect
            : rParam && rParam.startsWith("/")
            ? rParam
            : "/landmark");

        toast.success("Login verified.");
        await refreshAuth();
        console.log("[AuthPage] Verified. Navigating to:", nextPath);
        navigate(nextPath, { replace: true });
      } catch (e) {
        console.error(e);
        lastVerifiedTokenRef.current = null; // allow retry
        toast.error("Verification failed. Please request a new link.");
      } finally {
        setVerifying(false);
      }
    })();
  }, [token, rParam, navigate, refreshAuth]);

  // Framed by the Landmark Review platform: the frame has its own storage, so borrow the
  // sign-in from a first-party popup instead (see ReviewSignIn).
  const isFramed = useMemo(() => {
    try {
      return window.top !== window.self;
    } catch {
      return true;
    }
  }, []);
  useEffect(() => {
    if (!isFramed) return;
    const onMessage = async (e) => {
      if (e.origin !== window.location.origin || e.data?.type !== "bb-review-signin" || !e.data.token) return;
      localStorage.setItem("token", e.data.token);
      await refreshAuth();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [isFramed, refreshAuth]);

  // Framed by Landmark Review: it already knows who this is, so ask it for a one-time code
  // and sign in with that. The form only shows if the review can't vouch for them.
  const [handingOff, setHandingOff] = useState(isFramed && !token);
  useEffect(() => {
    if (!isFramed || token) return;
    let done = false;
    const finish = () => {
      done = true;
      setHandingOff(false);
    };
    const onMessage = async (e) => {
      if (done || e.source !== window.parent || !REVIEW_ORIGINS.includes(e.origin) || e.data?.type !== "bb-review-handoff") return;
      if (!e.data.code) return finish();
      done = true;
      try {
        const resp = await new ApiService().post("landmark/employees/auth/redeemReviewHandoff", { code: e.data.code });
        if (resp?.success && resp.sessionToken) {
          localStorage.setItem("token", resp.sessionToken);
          await refreshAuth();
          return;
        }
      } catch (err) {
        console.error(err);
      }
      setHandingOff(false);
    };
    window.addEventListener("message", onMessage);
    for (const origin of REVIEW_ORIGINS) {
      try {
        window.parent.postMessage({ type: "bb-review-handoff-request" }, origin);
      } catch {}
    }
    const timer = setTimeout(() => !done && finish(), 5000);
    return () => {
      done = true;
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
    };
  }, [isFramed, token, refreshAuth]);

  const openReviewSignIn = () => {
    const w = 480;
    const h = 640;
    const left = Math.max(0, (window.screen.width - w) / 2);
    const top = Math.max(0, (window.screen.height - h) / 2);
    const popup = window.open("/landmark/review-signin", "bb-review-signin", `width=${w},height=${h},left=${left},top=${top}`);
    if (!popup) toast.error("Your browser blocked the sign-in window. Allow pop-ups for this site and try again.");
  };

  const trimmedPrefix = emailPrefix.trim();

  const error = useMemo(() => {
    if (!touched) return "";
    if (!trimmedPrefix) return "Email is required.";
    if (!PREFIX_RE.test(trimmedPrefix))
      return "Only letters, numbers, dots, underscores, plus, percent, and hyphens are allowed.";
    return "";
  }, [trimmedPrefix, touched]);

  const isValid = !error && trimmedPrefix.length > 0;

  const fullEmail = isValid ? `${trimmedPrefix}@${AUTH_DOMAIN}` : "";

  const handleBlur = () => {
    if (trimmedPrefix.includes("@")) {
      const prefixOnly = trimmedPrefix.split("@")[0];
      setEmailPrefix(prefixOnly);
    } else {
      setEmailPrefix(trimmedPrefix);
    }
    setTouched(true);
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    const cleaned = raw.replace(/@.*$/, "");
    setEmailPrefix(cleaned);
    if (!touched) setTouched(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!isValid) return;

    setLoading(true);
    try {
      const api = new ApiService(apiOptions);
      const {
        success,
        error: apiError,
        localLoginLink,
        emailSent,
      } = await api.post(endpoint, {
        email: fullEmail,
        redirect: intendedRedirect, // include where the user currently is
      });

      if (!success) {
        toast.error(apiError || "Could not send login link. Please try again.");
        return;
      }
      const isValidLocalLoginLink =
        typeof localLoginLink === "string" &&
        localLoginLink.startsWith("http://localhost:3000/auth?token=");

      if (localLoginLink && isLocalClient && isValidLocalLoginLink) {
        try {
          await navigator.clipboard.writeText(localLoginLink);
          if (emailSent === false) {
            toast.success("Email failed locally. Magic link copied to clipboard.");
          } else {
            toast.success("Login link sent. Magic link copied to clipboard.");
          }
        } catch (_copyError) {
          if (emailSent === false) {
            toast.success("Email failed locally. Open magic link from backend logs.");
          } else {
            toast.success("Login link sent. Check your inbox.");
          }
        }
        return;
      }

      // Defensive: never expose/copy server-returned local links outside localhost.
      if (localLoginLink && !isLocalClient) {
        console.warn("[AuthPage] Ignoring localLoginLink outside localhost runtime");
      }

      toast.success("Login link sent. Check your inbox.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (handingOff) {
    return (
      <div className="landmark-sign-in">
        <div className="card">
          <div className="card-body">
            <div className="header-text mb-2">Landmark Sign In</div>
            <p className="mb-0">Signing you in with your Landmark Review sign-in…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="landmark-sign-in">
      <motion.div
        animate={{ scale: [0, 1.04, 1] }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="card">
          <div className="card-body">
            <div className="header-text mb-2">Landmark Sign In</div>
            <p className="mb-4">
              {verifying
                ? "Verifying your login link..."
                : "Enter your Landmark email address to receive a secure login link."}
            </p>

            <form onSubmit={handleSubmit} className="settings-form" noValidate>
              <div className="form-group mb-3">
                <label className="form-label" htmlFor="landmark-email-prefix">
                  Email
                </label>
                <div className="input-group input-primary">
                  <input
                    id="landmark-email-prefix"
                    type="text"
                    value={emailPrefix}
                    className={`form-control ${
                      touched && error ? "is-invalid" : touched && isValid ? "is-valid" : ""
                    }`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder=""
                    autoComplete="off"
                    aria-invalid={!!(touched && error)}
                    aria-describedby="emailHelpBlock"
                    required
                    disabled={verifying}
                  />
                  <span className="input-group-text">@{AUTH_DOMAIN}</span>
                </div>
                <small
                  id="emailHelpBlock"
                  className={`form-text ${touched && error ? "text-danger" : "text-muted"}`}
                >
                  {touched && error
                    ? error
                    : "Use your Landmark email prefix, for example mjones"}
                </small>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={verifying || loading || !isValid}
              >
                {verifying ? "Verifying..." : loading ? "Sending..." : "Get Login Link"}
              </button>
            </form>
            {isFramed && (
              <div className="mt-4 pt-3 border-top">
                <p className="mb-2">Already signed in to Better Blocks in this browser?</p>
                <button type="button" className="btn btn-outline-primary" onClick={openReviewSignIn}>
                  Use my Better Blocks sign-in
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPage;
